import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPageUrl } from "../utils/index.ts";
import { User } from "../api/supabaseEntities.js";
import {
  LayoutDashboard,
  Ticket,
  Users,
  Building2,
  CircleDollarSign,
  Settings,
  Bell,
  Menu,
  X,
  LogOut,
  FileText,
  Boxes,
  FolderKanban,
  BookOpen,
  BarChart3,
  HelpCircle,
  Search,
  KeyRound,
  Sparkles,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Calendar,
  Cpu,
  Zap,
  Home,
  Landmark,
  User as UserIcon,
  Star,
  Clock,
  Inbox,
  HeartPulse,
  ChevronsRight,
  ChevronsLeft,
  LifeBuoy,
  Shield,
  Cloud,
  Gauge,
  Lightbulb,
  Layers,
  Hexagon,
  Brain,
  ClipboardList,
  DollarSign,
  Heart
} from "lucide-react";
import { Button } from "../components/ui/button.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Input } from "../components/ui/input.jsx";
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu.jsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip.jsx";
import {
  DropdownMenu as QuickMenu,
  DropdownMenuTrigger as QuickMenuTrigger,
  DropdownMenuContent as QuickMenuContent,
  DropdownMenuItem as QuickMenuItem,
} from "../components/ui/dropdown-menu.jsx";

const menuGroups = [
  {
    title: "Principal",
    icon: Sparkles,
    gradient: "from-electric-400 to-electric-600",
    items: [
      { name: "Dashboard", icon: Home, path: "Dashboard", badge: null, gradient: "from-electric-400 to-neon-400" },
      { name: "Chamados", icon: Ticket, path: "Tickets", badge: null, gradient: "from-red-400 to-red-600" },
      { name: "Projetos", icon: FolderKanban, path: "Projects", badge: null, gradient: "from-sunset-400 to-sunset-600" },
      { name: "Base de Conhecimento", icon: BookOpen, path: "Knowledge", badge: null, gradient: "from-neon-400 to-electric-600" },
    ]
  },
  {
    title: "Recursos",
    icon: Layers,
    gradient: "from-magic-400 to-magic-600",
    items: [
      { name: "Sistemas", icon: Cpu, path: "Systems", admin: true, badge: null, gradient: "from-electric-400 to-electric-600" },
      { name: "Módulos do Sistema", icon: Hexagon, path: "SystemModules", admin: true, badge: null, gradient: "from-purple-400 to-purple-600" },
      { name: "Clientes", icon: Building2, path: "Clients", admin: true, badge: null, gradient: "from-neon-400 to-neon-600" },
      { name: "Usuários", icon: Users, path: "Users", admin: true, badge: null, gradient: "from-magic-400 to-magic-600" },

      { name: "Relatórios", icon: BarChart3, path: "Reports", admin: true, badge: null, gradient: "from-electric-400 to-magic-600" },
      { name: "Configurações", icon: Settings, path: "Settings", admin: true, badge: null, gradient: "from-magic-400 to-electric-600" },
    ]
  }
];

export default function Layout({ children, currentPageName, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarMode, setSidebarMode] = useState("expanded"); // collapsed, expanded, hidden
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Verificação se é página de login
  const currentUrl = window.location.pathname;
  const isLoginPageByUrl = currentUrl === '/' || currentUrl === '/AdminLogin' || currentUrl === '/ClientLogin' || currentUrl === '/ClientRecovery';

  const [dateRange, setDateRange] = useState("last7");
  const [searchOpen, setSearchOpen] = useState(false);
  const [compactHeader, setCompactHeader] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState(null);
  const [hoveredMenuItem, setHoveredMenuItem] = useState(null);

  useEffect(() => {
    // Checar preferência do usuário para modo da sidebar
    const storedSidebarMode = localStorage.getItem("sidebarMode");
    if (storedSidebarMode) {
      setSidebarMode(storedSidebarMode);
    } else if (window.innerWidth < 768) {
      setSidebarMode("hidden");
    }

    // Automaticamente mostrar a seção de menu baseada na página atual
    if (currentPageName && user) {
      menuGroups.forEach((group, index) => {
        if (group.items.some(item => item.path === currentPageName)) {
          setActiveMenuSection(index);
        }
      });
    }

    // Handle scroll for compact header
    const handleScroll = () => {
      setCompactHeader(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPageName, user]);



  const toggleSidebar = () => {
    const newMode = sidebarMode === "expanded" ? "collapsed" : "expanded";
    setSidebarMode(newMode);
    localStorage.setItem("sidebarMode", newMode);
  };

  const handleLogout = async () => {
    try {
      console.log('🚪 Iniciando processo de logout...');
      await User.signOut();
      console.log('✅ Logout realizado com sucesso, redirecionando...');
      
      // Limpar qualquer cache/estado adicional
      localStorage.removeItem('sidebarMode');
      
      // Redirecionar para a página de login
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Erro durante logout:', error);
      
      // Mesmo com erro, tentar redirecionar
      console.log('🔄 Redirecionando mesmo com erro...');
      window.location.href = '/';
    }
  };

  const toggleMenuSection = (index) => {
    setActiveMenuSection(activeMenuSection === index ? null : index);
  };

  // Se for página de login, renderizar sem layout
  if (isLoginPageByUrl) {
    return children;
  }

  // Se não tiver usuário (páginas protegidas), não renderizar (AuthGuard cuida disso)
  if (!user) return null;

  const isInternalUser = user.role !== 'client';
  const isAdmin = user.role === 'admin';
  
  // Logo moderna com efeito gradient
  const logoDisplay = sidebarMode === "collapsed" 
    ? <div className="relative">
        <Hexagon className="w-10 h-10 text-transparent bg-gradient-electric bg-clip-text animate-glow" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 bg-gradient-electric rounded-full animate-pulse"></div>
      </div>
              </div>
    : (
      <div className="flex items-center gap-3">
        <div className="relative">
          <Hexagon className="w-10 h-10 text-transparent bg-gradient-electric bg-clip-text animate-glow" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-gradient-electric rounded-full animate-pulse"></div>
                </div>
              </div>
        <div className="flex flex-col">
          <span className="font-bold text-2xl tracking-tight gradient-text">HelpDesk<span className="text-electric-500">Pro</span></span>
          <span className="text-xs text-muted-foreground -mt-1">v2.0 Neural</span>
                    </div>
                    </div>
    );

                  return (
    <div className="min-h-screen animated-bg">
        {/* Glassmorphism Header */}
        <header className={`fixed top-0 left-0 right-0 z-50 h-16 glass-card border-0 border-b border-white/10 transition-all duration-500 ${compactHeader ? 'h-12 backdrop-blur-3xl' : 'backdrop-blur-2xl'}`}>
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            {/* Left Section */}
            <div className="flex items-center gap-4">
                  {sidebarMode === "hidden" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSidebarMode("expanded")}
                  className="glass-card hover:glow-electric transition-all duration-300"
                    >
                      <Menu className="h-5 w-5" />
                    </Button>
                  )}
                  
              <div className="hidden md:flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Buscar tudo... (Ctrl+K)"
                    className="w-80 pl-10 glass-card border-white/20 focus:glow-electric transition-all duration-300"
                    onClick={() => setSearchOpen(true)}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <kbd className="px-2 py-1 text-xs bg-muted rounded border text-muted-foreground">⌘K</kbd>
                  </div>
                </div>
                  </div>
                </div>

            {/* Center - Page Title */}
            <div className="hidden lg:flex items-center">
              <h1 className="text-xl font-semibold gradient-text">{currentPageName}</h1>
                </div>

            {/* Right Section */}
                <div className="flex items-center gap-2">
              {/* Quick Actions */}
              <div className="hidden md:flex items-center gap-2">
                <QuickMenu>
                  <QuickMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="glass-card hover:glow-neon transition-all duration-300"
                      aria-label="Ações rápidas"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </QuickMenuTrigger>
                  <QuickMenuContent align="end" className="w-48 glass-card border-white/20">
                    <QuickMenuItem onClick={() => navigate(createPageUrl('NewTicket'))}>
                      <Ticket className="mr-2 h-4 w-4 text-electric-500" />
                      Novo Chamado
                    </QuickMenuItem>
                    <QuickMenuItem onClick={() => navigate(createPageUrl('NewProject'))}>
                      <FolderKanban className="mr-2 h-4 w-4 text-magic-500" />
                      Novo Projeto
                    </QuickMenuItem>
                    <QuickMenuItem onClick={() => navigate(createPageUrl('NewClient'))}>
                      <Building2 className="mr-2 h-4 w-4 text-neon-500" />
                      Novo Cliente
                    </QuickMenuItem>
                  </QuickMenuContent>
                </QuickMenu>
              </div>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full glass-card hover:glow-magic transition-all duration-300">
                    <Avatar className="h-10 w-10 border-2 border-magic-500/50">
                      <AvatarImage src={user.avatar_url} alt={user.name} />
                      <AvatarFallback className="bg-gradient-magic text-white font-bold">
                        {user.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-neon-500 rounded-full border-2 border-background animate-pulse"></div>
                      </Button>
                    </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 glass-card border-white/20">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none gradient-text">{user.name || user.email}</p>
                          <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                        <div className="flex items-center gap-2 pt-1">
                          <div className={`w-2 h-2 rounded-full animate-pulse ${user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <span className={`text-xs font-medium ${user.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>{user.status === 'active' ? 'Ativo' : 'Inativo'}</span>
                          <div className="w-2 h-2 bg-neon-500 rounded-full animate-pulse ml-2"></div>
                          <span className="text-xs text-neon-500 font-medium">Online</span>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={() => navigate(createPageUrl('UserProfile'))}
                    className="cursor-pointer hover:bg-magic-500/10"
                  >
                        <UserIcon className="mr-2 h-4 w-4" />
                    <span>Perfil</span>
                      </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate(createPageUrl('Settings'))}
                    className="cursor-pointer hover:bg-electric-500/10"
                  >
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Configurações</span>
                      </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer hover:bg-red-500/10 text-red-500"
                  >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sair</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </header>

        <div className="flex pt-16">
          {/* Modern Sidebar */}
          {sidebarMode !== "hidden" && (
            <aside className={`fixed left-0 top-16 bottom-0 z-40 glass-card border-0 border-r border-white/10 transition-all duration-500 ${
              sidebarMode === "expanded" ? "w-72" : "w-20"
            }`}>
              <div className="flex flex-col h-full">
                {/* Logo Section */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    {logoDisplay}
                    {sidebarMode === "expanded" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleSidebar}
                        className="glass-card hover:glow-electric transition-all duration-300"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                    )}
                    {sidebarMode === "collapsed" && (
                <Button
                  variant="ghost"
                  size="icon"
                        onClick={toggleSidebar}
                        className="glass-card hover:glow-electric transition-all duration-300 absolute -right-3 top-1/2 transform -translate-y-1/2"
                >
                        <ChevronsRight className="h-4 w-4" />
                </Button>
                    )}
                  </div>
                </div>
                
                {/* Navigation */}
                <ScrollArea className="flex-1 px-3 py-4">
                  <nav className="space-y-2">
                  {menuGroups.map((group, groupIndex) => {
                      const groupItems = group.items.filter(item => 
                        !item.admin || (item.admin && isAdmin)
                    );

                      if (groupItems.length === 0) return null;

                    return (
                        <div key={groupIndex} className="space-y-1">
                          {sidebarMode === "expanded" && (
                            <Button
                              variant="ghost"
                              className="w-full justify-between glass-card hover:glow transition-all duration-300 group"
                              onClick={() => toggleMenuSection(groupIndex)}
                            >
                              <div className="flex items-center gap-3">
                                <group.icon className={`h-4 w-4 bg-gradient-to-r ${group.gradient} bg-clip-text text-transparent`} />
                                <span className="font-medium">{group.title}</span>
                              </div>
                              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${
                                activeMenuSection === groupIndex ? 'rotate-180' : ''
                              }`} />
                            </Button>
                          )}
                          
                          <div className={`space-y-1 transition-all duration-300 overflow-hidden ${
                            sidebarMode === "collapsed" || activeMenuSection === groupIndex 
                              ? 'max-h-96 opacity-100' 
                              : sidebarMode === "expanded" 
                                ? 'max-h-0 opacity-0' 
                                : 'max-h-96 opacity-100'
                          }`}>
                            {groupItems.map((item) => {
                          const isActive = currentPageName === item.path;
                              const ItemIcon = item.icon;
                              
                          return (
                                <Tooltip key={item.path}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      className={`w-full ${sidebarMode === "collapsed" ? "justify-center p-3" : "justify-start"} 
                                        relative group transition-all duration-300 glass-card hover:glow
                                        ${isActive ? 'bg-gradient-to-r from-white/10 to-white/5 glow-electric' : ''}
                                      `}
                                      onClick={() => navigate(createPageUrl(item.path))}
                                      onMouseEnter={() => setHoveredMenuItem(item.path)}
                                      onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                      <div className={`flex items-center gap-3 ${sidebarMode === "collapsed" ? "justify-center" : ""}`}>
                                        <ItemIcon className={`h-5 w-5 transition-all duration-300 ${
                                          isActive || hoveredMenuItem === item.path
                                            ? `bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent`
                                            : ''
                                        }`} />
                                        {sidebarMode === "expanded" && (
                                          <span className={`font-medium transition-all duration-300 ${
                                            isActive ? 'gradient-text' : ''
                                          }`}>
                                            {item.name}
                                          </span>
                                        )}
                                      </div>
                                      
                                      {item.badge && sidebarMode === "expanded" && (
                                        <div className={`px-2 py-1 rounded-full text-xs font-bold text-white ${item.badge.color} animate-pulse`}>
                                  {item.badge.value}
                                        </div>
                                      )}
                                      
                                      {item.badge && sidebarMode === "collapsed" && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-bounce"></div>
                                      )}
                                      
                                      {isActive && (
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-electric-400 to-magic-600 rounded-r animate-pulse"></div>
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  {sidebarMode === "collapsed" && (
                                    <TooltipContent side="right" className="glass-card border-white/20">
                                      <p className="font-medium">{item.name}</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                          );
                        })}
                          </div>
                      </div>
                    );
                  })}
                </nav>
                </ScrollArea>

                {/* Bottom Section */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      {sidebarMode === "expanded" && (
                        <div className="text-xs text-muted-foreground">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 bg-neon-500 rounded-full animate-pulse"></div>
                            <span>Sistema Neural Ativo</span>
                  </div>
                          <div className="text-xs opacity-70">v2.0.1 • Build 2024.1</div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="glass-card hover:glow-magic transition-all duration-300"
                    >
                      <LifeBuoy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
            </div>
            </aside>
          )}

          {/* Main Content */}
          <main className={`flex-1 transition-all duration-500 ${
            sidebarMode === "expanded" ? "ml-72" : 
            sidebarMode === "collapsed" ? "ml-20" : 
            "ml-0"
          }`}>
            <div className="p-6 space-y-6 min-h-screen">
              <div className="animate-fade-in">
                {children}
              </div>
              </div>
            </main>
          </div>
        </div>
  );
}

